import {
  take,
  put,
  select,
  apply,
  delay,
  spawn,
  race,
  takeLatest,
  fork,
} from 'redux-saga/effects';
import {
  BetshopConfigRequest,
} from 'bcms-api';
import updateBcmsConfig from 'actions/bcmsConfig';
import {
  paymentCheckSuccess,
  paymentCheckFailed,
  loggedInSuccess,
  loggedInFailure,
  accountDisabled,
  accountEnabled,
  setBId,
} from 'reducers/authorization';
import {
  START_LCR,
  BCMS_BETSHOP_CONFIG_LOAD_SUCCESS,
} from 'constants/actions';
import runLoggerSaga from 'sagas/logger';
import {
  getUserIdFromUrl,
  redirectCurrentHostToClient,
  redirectToLogin,
} from 'utils/Url';
import CheckAuthorization from 'auth-api';
import { DisplayError } from 'utils/LoggerItem';
import loadPlayer from 'sagas/player';
import loadSir from 'sagas/sir';
import fishnetSaga from 'sagas/fishnet/fishnet';
import fishnetCleanup from 'sagas/fishnet/fishnetCleanup';
import { CHANNELS_ESOCCER_ONLY, SEC } from 'constants/app';
import { sport } from 'constants/enum';
import {
  HTTP_STATUS_BAD_GATEWAY,
  HTTP_STATUS_CODE_LOCKED,
  HTTP_STATUS_CODE_PAYMENT_REQUIRED,
  HTTP_STATUS_NOT_FOUND,
  HTTP_STATUS_SERVICE_UNAVAILABLE,
} from 'constants/bcms.js';
import {
  getAuthPollingInterval,
  getLoggedInStatus,
  getBetshopId,
  getAutoSport,
  getChannelId,
  getIsOperator,
  getTechnicalIssuesStatus,
  getClientId,
} from '../reducers';
import { makeRestartable, sagaCancel } from './utilSagas';
import onBCMSChange from './bcms';
import monitorStage from './stage';
import autoSportSaga from './autosport';
import runLoginAuthCheckSaga from './loginAuthCheck/loginAuthCheck';
import { setTechnicalIssuesStatus } from '../actions/stages';

function* authorizationStatusCheck(betshopUserId) {
  try {
    const pollInterval = yield select(getAuthPollingInterval);
    let usedDelay = pollInterval;

    for (;;) {
      const isOperatorStatus = yield select(getIsOperator);
      const technicalIssues = yield select(getTechnicalIssuesStatus);

      const request = new CheckAuthorization('/auth/validate', betshopUserId, isOperatorStatus);
      const response = yield apply(request, request.get);

      const {
        isAuthorized, userId = null, isOperator, bId,
      } = response;

      if (response
        && [HTTP_STATUS_NOT_FOUND, HTTP_STATUS_SERVICE_UNAVAILABLE, HTTP_STATUS_BAD_GATEWAY].includes(response.code)
      ) {
        if (technicalIssues === null) {
          yield put(setTechnicalIssuesStatus(true));
        }
      } else if (!isAuthorized) {
        if (response.code === HTTP_STATUS_CODE_LOCKED) {
          yield put(setTechnicalIssuesStatus(false));
          yield put(accountDisabled());
          yield put(loggedInFailure());
        }

        yield put(setTechnicalIssuesStatus(false));
        yield put(loggedInFailure());
        redirectToLogin();
        return;
      } else if (response && !isOperator && userId && isAuthorized && userId !== getUserIdFromUrl()) {
        redirectCurrentHostToClient(userId);
        return;
      } else if (isAuthorized?.error) {
        // if error dont do anything
        // ie. bcms down, keep running if been auth
        if (yield select(getLoggedInStatus) === null) { // if first time
          yield put(loggedInFailure());
        }
      } else if (isAuthorized) {
        yield put(setTechnicalIssuesStatus(false));
        yield put(loggedInSuccess(isOperator));
        yield put(setBId(bId));
        yield put(accountEnabled());
        usedDelay = pollInterval;
      }

      yield race([
        take(START_LCR),
        delay(usedDelay * SEC),
      ]);
    }
  } catch (error) {
    yield put(loggedInFailure());
    throw error;
  }
}

function* pollBetshopConfig(userId) {
  for (;;) {
    const technicalIssues = yield select(getTechnicalIssuesStatus);

    if (technicalIssues) {
      // eslint-disable-next-line no-continue
      continue;
    }

    const isOperator = yield select(getIsOperator);
    const clientId = yield select(getClientId);
    const betshopId = yield select(getBetshopId);

    const request = new BetshopConfigRequest(userId, clientId, betshopId, isOperator);
    const response = yield apply(request, request.get);

    if (response && response.betshopConfig) {
      yield put(paymentCheckSuccess());
      yield put(updateBcmsConfig(response.betshopConfig));
    } else if (response && response.code === HTTP_STATUS_CODE_PAYMENT_REQUIRED) {
      yield put(paymentCheckFailed());
    } else {
      // other errors dont do anything
      // ie. bcms down, keep running if been logged in
    }
    yield delay(request.getMaxAge() * SEC);
  }
}

function* onAutoSport() {
  yield take(BCMS_BETSHOP_CONFIG_LOAD_SUCCESS);
  let saga = null;
  let autoSport = yield select(getAutoSport);
  const channelId = yield select(getChannelId);

  let prev = autoSport;
  if (autoSport) {
    saga = yield fork(autoSportSaga, autoSport);
  } else if (CHANNELS_ESOCCER_ONLY.includes(channelId)) {
    saga = yield fork(autoSportSaga, sport.esoccer);
  }

  yield takeLatest(START_LCR, function* () {
    autoSport = yield select(getAutoSport);
    if (autoSport !== prev) {
      prev = autoSport;
      saga = sagaCancel(saga);
      if (autoSport) {
        saga = yield fork(autoSportSaga, autoSport);
      } else if (CHANNELS_ESOCCER_ONLY.includes(channelId)) {
        saga = yield fork(autoSportSaga, sport.esoccer);
      }
    }
  });
}

export function* userSaga(userId) {
  const restartAble = [];

  try {
    restartAble.push(yield spawn(makeRestartable(null, authorizationStatusCheck, userId)));

    const { type } = yield take([loggedInSuccess.type, loggedInFailure.type]);
    if (type === loggedInFailure.type) {
      yield put(paymentCheckFailed()); // show logged in failed screen
      yield take(loggedInSuccess.type);
    }

    restartAble.push(yield spawn(makeRestartable(null, onBCMSChange, userId)));
    restartAble.push(yield spawn(makeRestartable(null, monitorStage)));
    restartAble.push(yield spawn(makeRestartable(null, pollBetshopConfig, userId)));
    restartAble.push(yield spawn(makeRestartable(null, fishnetSaga)));
    restartAble.push(yield spawn(makeRestartable(null, fishnetCleanup)));


    yield fork(onAutoSport);

    yield take(loggedInFailure.type);

    throw new DisplayError('User', 'Logged Out');
  } finally {
    for (let i = 0; i < restartAble.length; i++) {
      const saga = restartAble[i];
      sagaCancel(saga);
    }
  }
}

function* lcrSaga() {
  let userId = null;
  let saga = null;

  yield spawn(makeRestartable(null, runLoginAuthCheckSaga));
  let { payload: { userId: newUserId } } = yield take(START_LCR);

  // spawn detached, respawns if they error
  yield spawn(makeRestartable(null, runLoggerSaga));
  yield spawn(makeRestartable(null, loadPlayer));
  yield spawn(makeRestartable(null, loadSir));

  for (;;) {
    if (newUserId && newUserId !== userId) {
      userId = newUserId;
      saga = sagaCancel(saga);
      saga = yield spawn(makeRestartable({ maxWait: 30 * SEC }, userSaga, userId));
    }
    ({ payload: { userId: newUserId } } = yield take(START_LCR));
  }
}

export default lcrSaga;
